import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../pack-icons';
import { FONT_CATALOG, loadGoogleFont, previewLetters } from '../../lib/brand-typography.js';

function FontPreview({ font, brandName, letters }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let active = true;
    loadGoogleFont(font.family, `${brandName}${letters}0123456789`)
      .then(() => { if (active) setLoaded(true); })
      .catch(() => { if (active) setLoaded(false); });
    return () => { active = false; };
  }, [font.family, brandName, letters]);
  const style = loaded ? { fontFamily: `"${font.family}"` } : undefined;
  return (
    <span className="tycat-spec" style={style}>
      <b>{brandName}</b><span>{letters}</span><span>0123456789</span>
    </span>
  );
}

export default function FontCatalogModal({ open, brandName, uiLanguage, needsCyrillic, rendering, error, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  useEffect(() => { if (open) setSelected(null); }, [open]);
  if (!open) return null;
  const letters = previewLetters(uiLanguage);
  const groups = ['serif', 'sans', 'display', 'script'];
  return (
    <div className="tycat-bg" onClick={rendering ? undefined : onClose}>
      <div className="tycat-modal card" role="dialog" aria-modal="true" aria-labelledby="tycat-title" onClick={(event) => event.stopPropagation()}>
        <header className="tycat-head">
          <div><h2 id="tycat-title">{t('typography.catalogTitle')}</h2><p>{t('typography.catalogIntro')}</p></div>
          <button type="button" className="tycat-close" onClick={onClose} disabled={rendering} aria-label={t('typography.close')}><I.x size={17} /></button>
        </header>
        <div className="tycat-body">
          {groups.map((group) => (
            <section key={group} className="tycat-group">
              <h3>{t(`typography.group_${group}`)}</h3>
              <div className="tycat-grid">
                {FONT_CATALOG.filter((font) => font.group === group).map((font) => {
                  const disabled = needsCyrillic && !font.coverage.includes('cyrillic');
                  const active = selected?.family === font.family;
                  return (
                    <button
                      key={font.family}
                      type="button"
                      className={`tycat-card ${active ? 'is-on' : ''}`}
                      disabled={disabled || rendering}
                      aria-pressed={active}
                      title={disabled ? t('typography.latinDisabled') : undefined}
                      onClick={() => setSelected(font)}
                    >
                      <FontPreview font={font} brandName={brandName || t('typography.yourBrand')} letters={letters} />
                      <span className="tycat-meta"><b>{font.family}</b><span>{font.coverage.includes('cyrillic') ? t('typography.cyrillicCoverage') : t('typography.latinOnly')}</span></span>
                      {disabled && <span className="tycat-disabled">{t('typography.unavailable')}</span>}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
        <footer className="tycat-foot">
          <span className="tycat-footnote">{error || (needsCyrillic ? t('typography.cyrillicNeeded') : t('typography.coverageHint'))}</span>
          <button type="button" className="btn btn-primary" disabled={!selected || rendering} onClick={() => onConfirm(selected)}>
            {rendering ? <><span className="bk-gspin" aria-hidden="true" /> {t('typography.rendering')}</> : (selected ? t('typography.useFont', { font: selected.family }) : t('typography.selectFont'))}
          </button>
        </footer>
      </div>
    </div>
  );
}
